'use client';

import { GA_CONFIG } from '@/lib/analytics/ga-config';
import { usePathname, useSearchParams } from 'next/navigation';
import { Suspense, useEffect } from 'react';

// Google Analytics 4 Script Component
function GoogleAnalyticsInner() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    // Only run if analytics is enabled
    if (!GA_CONFIG.ENABLE_ANALYTICS || typeof window === 'undefined') return;

    // Initialize GA4
    const initGA4 = () => {
      // Check if gtag is already loaded
      if (typeof window.gtag === 'undefined') {
        // Load gtag script
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_CONFIG.MEASUREMENT_ID}`;
        document.head.appendChild(script);

        // Initialize gtag
        window.dataLayer = window.dataLayer || [];
        window.gtag = function () {
          window.dataLayer.push(arguments);
        };

        // Configure GA4
        window.gtag('js', new Date());
        window.gtag('config', GA_CONFIG.MEASUREMENT_ID, {
          page_title: document.title,
          page_location: window.location.href,
          send_page_view: false, // We'll handle page views manually
        });
      }
    };

    // Track page view
    const trackPageView = () => {
      if (typeof window.gtag !== 'undefined') {
        window.gtag('event', 'page_view', {
          page_title: document.title,
          page_location: window.location.href,
          page_path: pathname,
          page_category: getPageCategory(pathname),
          content_type: getContentType(pathname),
        });
      }
    };

    // Get page category based on pathname
    const getPageCategory = (path: string): string => {
      if (path === '/') return GA_CONFIG.PAGE_CATEGORIES.HOME;
      if (path.startsWith('/blogs')) return GA_CONFIG.PAGE_CATEGORIES.BLOG;
      if (path.startsWith('/web-development-services') || path.startsWith('/mobile-app-development-services')) return GA_CONFIG.PAGE_CATEGORIES.SERVICES;
      if (path.startsWith('/about-us')) return GA_CONFIG.PAGE_CATEGORIES.ABOUT;
      if (path.startsWith('/contact-us')) return GA_CONFIG.PAGE_CATEGORIES.CONTACT;
      if (path.startsWith('/case-studies')) return GA_CONFIG.PAGE_CATEGORIES.CASE_STUDIES;
      if (path.startsWith('/careers')) return GA_CONFIG.PAGE_CATEGORIES.CAREERS;
      return 'other';
    };

    // Get content type based on pathname
    const getContentType = (path: string): string => {
      if (path === '/') return GA_CONFIG.CONTENT_TYPES.LANDING_PAGE;
      if (path.startsWith('/blogs/')) return GA_CONFIG.CONTENT_TYPES.BLOG_POST;
      if (path.startsWith('/web-development-services') || path.startsWith('/mobile-app-development-services')) return GA_CONFIG.CONTENT_TYPES.SERVICE_PAGE;
      if (path.startsWith('/contact-us')) return GA_CONFIG.CONTENT_TYPES.CONTACT_FORM;
      return GA_CONFIG.CONTENT_TYPES.LANDING_PAGE;
    };

    // Initialize GA4 if not already loaded
    if (typeof window.gtag === 'undefined') {
      initGA4();
    }

    // Track page view when pathname or search params change
    trackPageView();
  }, [pathname, searchParams]);

  // Return null as this component doesn't render anything
  return null;
}

// Main component with Suspense boundary
export default function GoogleAnalytics() {
  return (
    <Suspense fallback={null}>
      <GoogleAnalyticsInner />
    </Suspense>
  );
}

// Extend Window interface for gtag
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}
